import { FontIcon, getTheme } from '@fluentui/react';
import AppContext from 'App/AppContext';
import {
  IPDefaultButton,
  IPIconButton,
  IPModal,
  IPPivot,
  IPPivotItem,
  IPPrimaryButton,
  IPScrollablePane,
  IPSeparator,
  IPText,
} from 'baseComponents';
import { IPScrollbarVisibility } from 'baseComponents/ScrollablePane/IPScrollablePane';
import { IPStack } from 'baseComponents/Stack/IPStack';
import ActivityTrail from 'components/Activity/ActivityTrail';
import AuditTrail from 'components/Audit/AuditTrail';
import AuditCriteriaEditorModal from 'components/AuditCriteria/AuditCriteriaEditorModal';
import DialogConfirmDelete from 'components/Dialogs/DialogConfirmDelete';
import DialogYesNo from 'components/Dialogs/DialogYesNo';
import TaskLinkList from 'components/Links/LinkLists/TaskLinkList';
import OverlayLoader from 'components/Loading/OverlayLoader';
import { ErrorMessage } from 'components/Notification/ErrorMessage';
import InfoMessage from 'components/Notification/InfoMessage';
import { cancelIcon, complianceIcon, globalStackStylesHeight100, globalStackTokensGapSmall } from 'globalStyles';
import Joi from 'joi';
import { auditEntityKeyString, getAuditEntityKeysOfTask } from 'models/auditCriteria';
import { AuthSchemaLineOperation } from 'models/auth/authSchemaLine';
import {
  ControlSceneAIActionsPreviewFeatureToggleCode,
  isPreviewFeatureToggleEnabled,
} from 'models/copilot/copilotPreviewFeatureToggles';
import { EntityTypes } from 'models/entity';
import Task, { TaskTypes } from 'models/tasks/task';
import TaskType from 'models/tasks/taskType';
import { Fragment, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { apiGetAuditProcedureName } from 'services/Api/internalAuditService';
import { apiGetTaskContext } from 'services/Api/taskService';
import { apiRequest } from 'services/Auth/authConfig';
import { hasUserDataPermission, hasUserFeatureGenericManager } from 'services/Auth/featurePermissions';
import { EmbeddedTaskTypeFormEditor } from '../../Forms/EmbeddedTaskTypeFormEditor';
import { ISingleTaskTypeProps } from '../../SingleTask';
import TaskTopTasksBar from '../../TaskTopTasksBar';
import { getTaskViewModalStackStyles, getTaskViewModalStyles } from '../../View/TaskRenderHelpers';
import { TaskDetailOwner } from '../Details/TaskDetailOwner';
import { AuditTemplateTaskDetail } from './AuditTemplateTaskDetail';

interface IAuditTemplateTaskProps extends ISingleTaskTypeProps {
  onSaveAsNewTask: () => void;
  onChangeCompletionDate: (completionDate: Date) => void;
}

// The guided editor for an internal audit procedure library: the EventTemplate header holds the
// procedures (its subtasks), which the audit's execute phase matches to scope items. The layout
// switches parent/child on isSeries()
const AuditTemplateTask = (props: IAuditTemplateTaskProps) => {
  //forms: the generated form's generic label, resolved through TaskType.forTask
  const { t } = useTranslation(['task', 'audit', 'control', 'translation', 'dateTimeComponent', 'forms']);
  const appContext = useContext(AppContext);
  const [err, setErr] = useState<Joi.ValidationError | undefined>(undefined);
  const [selectedPivot, setSelectedPivot] = useState<string>(
    props.selectedPivot ?? (props.task.isSeries() ? 'Resources' : 'Context'),
  );
  const [showRemoveDialog, setShowRemoveDialog] = useState<boolean>(false);
  const [showCancelDialog, setShowCancelDialog] = useState<boolean>(false);
  // The names of the entities this procedure covers, keyed the way the projected form fields are:
  // they head each entity's questions on the form and anchor the criteria commands in the editor.
  const [entityNamesByKey, setEntityNamesByKey] = useState<Map<string, string>>(new Map());
  // Two procedures should not carry one name: the user picks a procedure out of a list of names. This
  // check is the whole rule (best effort, client-side only since 2026-09-11) - the server keeps the
  // name lookup but no longer refuses a duplicate on save, so what the user sees under this field is
  // the one place a collision is reported.
  const [nameError, setNameError] = useState<string | undefined>(undefined);
  const nameCheckTimer = useRef<number | undefined>(undefined);
  const nameCheckSeq = useRef<number>(0);
  const theme = getTheme();

  // The scope items this procedure covers, read off the task itself: an unsaved procedure has them
  // too, and its questions are the criteria OF those items.
  const linkedEntityKeys = getAuditEntityKeysOfTask(props.task);
  const hasForm = !!props.task.taskTypeId && props.task.taskTypeId > 0;
  // A procedure without a form yet shows its linked items' criteria where the form will be: they
  // can be reviewed, added or drafted before the save that makes the server generate the form from
  // them. Mutually exclusive with the form tab, which needs the form.
  const showCriteriaTab = !props.task.isSeries() && props.hasFormFeature && !hasForm && linkedEntityKeys.length > 0;
  const showFormOnSaveHint =
    !props.task.isSeries() && props.hasFormFeature && !hasForm && linkedEntityKeys.length === 0;
  // The same gate as the criteria commands of the form tab (TaskTypeFormEditor.canManageCriteria):
  // where those are offered after the save, the criteria are editable before it. Read-only otherwise.
  const canManageCriteria =
    isPreviewFeatureToggleEnabled(appContext.globalDataCache.settings, ControlSceneAIActionsPreviewFeatureToggleCode) &&
    hasUserFeatureGenericManager(appContext);

  // The names of the items linked in this session come from the picker's entities on the task: the
  // context read below needs a saved task, and a link made here is not saved yet.
  const allEntityNamesByKey = useMemo(() => {
    const names = new Map(entityNamesByKey);
    (props.task.relatedEntities ?? []).forEach((entity) => {
      if (!entity.isDeleted && entity.entityName) {
        names.set(auditEntityKeyString(entity.typeOfEntity, entity.entityId), entity.entityName);
      }
    });

    return names;
  }, [entityNamesByKey, props.task.relatedEntities]);

  // Where the right pane opens: the form when there is one, the linked items' criteria while the
  // form does not exist yet, the library otherwise (and always for the procedure library itself).
  const defaultPivot = (): string => {
    if (props.task.isSeries()) return 'Resources';
    if (hasForm && props.hasFormFeature) return 'TaskType';
    if (showCriteriaTab) return 'Criteria';

    return 'Resources';
  };

  useEffect(() => {
    const loadEntityNames = async () => {
      if (props.task.taskId <= 0) return;

      // best effort: the sections are display sugar, the form works without the names
      try {
        const accessToken = await appContext.getAccessToken(apiRequest.scopes);
        const context = await apiGetTaskContext(props.task.taskId, accessToken, appContext);
        const names = new Map<string, string>();
        const displayName = (code: string | undefined, name: string) => [code, name].filter(Boolean).join(' ');
        context.controls.forEach((item) =>
          names.set(
            auditEntityKeyString(EntityTypes.Control as number, item.controlId),
            displayName(item.code, item.name),
          ),
        );
        context.risks.forEach((item) =>
          names.set(auditEntityKeyString(EntityTypes.Risk as number, item.riskId), displayName(item.code, item.name)),
        );
        context.processes.forEach((item) =>
          names.set(
            auditEntityKeyString(EntityTypes.Process as number, item.processId),
            displayName(item.code, item.name),
          ),
        );
        context.objectives.forEach((item) =>
          names.set(
            auditEntityKeyString(EntityTypes.Objective as number, item.objectiveId),
            displayName(item.code, item.name),
          ),
        );
        context.assets.forEach((item) =>
          names.set(auditEntityKeyString(EntityTypes.Asset as number, item.assetId), displayName(item.code, item.name)),
        );
        context.processingActivities.forEach((item) =>
          names.set(
            auditEntityKeyString(EntityTypes.ProcessingActivity as number, item.processingActivityId),
            displayName(item.code, item.name),
          ),
        );
        setEntityNamesByKey(names);
      } catch {
        setEntityNamesByKey(new Map());
      }
    };

    void loadEntityNames();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props.task.taskId]);

  // Joined to one string so the check below keys on the names themselves, not on the array the parent
  // rebuilds every render.
  const siblingKey = (props.siblingNames ?? []).join('\n');

  useEffect(() => {
    // Only a LIBRARY PROCEDURE has to have a unique name. This component also renders the library
    // header (no name field of its own) and the audit's own execution tasks, which reach it through
    // TaskKinds.InternalAudit as well - and an execution task is deliberately named after the
    // procedure it was built from, so checking it would report the procedure as a clash and disable
    // a Save that the server would happily accept.
    if (props.task.taskType !== TaskTypes.EventTemplate || props.task.isSeries()) return;

    // Newest check wins: cancel the pending call and invalidate whatever is still in flight, so a
    // stale "free" answer can never clear an error the current name really has.
    if (nameCheckTimer.current) window.clearTimeout(nameCheckTimer.current);
    const seq = ++nameCheckSeq.current;
    const typed = (props.task.name ?? '').trim();

    // An empty name is the Joi rule's problem, not a duplicate. A saved procedure that still carries
    // the name it was loaded with cannot collide with itself either.
    if (!typed || (props.task.taskId > 0 && typed.toLowerCase() === (props.orgTask.name ?? '').trim().toLowerCase())) {
      setNameError(undefined);

      return;
    }

    // The siblings the library holds in memory, unsaved ones included: two procedures added in one
    // editor session are both new, so the server below cannot know about either - this list is the
    // only place they meet. Answered at once, no round trip.
    if ((props.siblingNames ?? []).some((name) => (name ?? '').trim().toLowerCase() === typed.toLowerCase())) {
      setNameError(t('audit:Procedure.NameExists'));

      return;
    }

    nameCheckTimer.current = window.setTimeout(async () => {
      try {
        const accessToken = await appContext.getAccessToken(apiRequest.scopes);
        const existingTaskId = await apiGetAuditProcedureName(typed, accessToken);
        if (seq !== nameCheckSeq.current) return;

        setNameError(existingTaskId && existingTaskId !== props.task.taskId ? t('audit:Procedure.NameExists') : undefined);
      } catch {
        // A failed check keeps the current verdict: the save still refuses a real duplicate, and
        // blocking Save on a network hiccup would be worse than letting it through to that refusal.
      }
    }, 400);

    return () => {
      if (nameCheckTimer.current) window.clearTimeout(nameCheckTimer.current);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props.task.name, props.task.taskId, props.task.taskType, props.orgTask.name, siblingKey]);

  useEffect(() => {
    const _localizedFields = {
      assignment: t('task:Assignment.Label'),
      startDateTime: t('task:Start.Label'),
      endDateTime: t('task:End.ErrorMessage'),
      name: t('task:Name.Label'),
      deadline: t('task:Deadline.ErrorMessage'),
    };

    const result = props.task.validate(_localizedFields);
    setErr(result.error);
  }, [t, props.task]);

  useEffect(() => {
    if (appContext.isMobileView) {
      setSelectedPivot('TaskDetails');
    } else {
      setSelectedPivot(defaultPivot());
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [appContext.isMobileView]);

  useEffect(() => {
    //Single task requests to show a specific pivot. Neither 'Checklist' nor 'Context' is one of ours -
    //a procedure asks its questions on the form and carries its scope items under the description - so
    //both are answered with the tab that holds the questions (the procedures of a series live on
    //Resources) instead of leaving the user on a tab that does not exist
    if (props.selectedPivot === 'Checklist' || props.selectedPivot === 'Context') {
      setSelectedPivot(defaultPivot());
    } else if (props.selectedPivot) {
      setSelectedPivot(props.selectedPivot);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props.selectedPivot]);

  // Linking the first item brings its questions into view at once; unlinking the last one takes the
  // tab away, so whoever was on it lands on the library rather than on a tab that is gone. Mobile
  // keeps its own first tab (the details) on open.
  useEffect(() => {
    if (showCriteriaTab && !appContext.isMobileView) {
      setSelectedPivot('Criteria');
    } else if (!showCriteriaTab && selectedPivot === 'Criteria') {
      setSelectedPivot('Resources');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showCriteriaTab]);

  const validate = (fieldName: string): string | undefined => {
    if (fieldName === 'name' && nameError) {
      return nameError;
    }

    if (err) {
      const fieldError = err.details.find((e) => {
        return e.context?.key === fieldName || e.message.toLowerCase().includes(fieldName.toLowerCase());
      });

      return fieldError ? fieldError.message : undefined;
    }

    return undefined;
  };

  const hasErrors = (): boolean => {
    return err !== undefined || nameError !== undefined;
  };

  // The procedure's form. A generated form is hidden: the login cache holds it, a cache refreshed from
  // GET tasks/types does not, so on a miss this is a stand-in carrying the real id - which is all the
  // editor needs (see TaskType.forTask).
  const formTaskType = TaskType.forTask(
    props.task.taskTypeId,
    appContext,
    t,
    props.task.taskTypeFamilyId,
    props.task.formVersion,
  );

  // The procedure's form forked into a new version and the server repointed this task to it. The task
  // follows: its form is read back by the id the task carries, and the version it left is refused.
  // Only the version moves - the family the task carries is the server's to decide.
  const onFormVersionChanged = (newTaskType: TaskType) => {
    if (newTaskType.taskTypeId === props.task.taskTypeId) return;

    const newTask = props.task.clone();
    newTask.taskTypeId = newTaskType.taskTypeId;
    newTask.formVersion = newTaskType.clone();
    props.onUpdateTaskForForm(newTask, false);
  };

  const cannotSaveTask = (): boolean => {
    return (props.task.taskId !== -1 && props.task.isEqual(props.orgTask)) || hasErrors();
  };

  const onCancel = () => {
    if (cannotSaveTask()) {
      props.onCancel();
    } else {
      setShowCancelDialog(true);
    }
  };

  // No Form command on a procedure, new or saved. Its form is not chosen from the catalogue: the
  // server generates a hidden one from the criteria of the scope item the procedure is linked to
  // (on save, and again whenever those criteria change) and it is fixed from then on - the recorded
  // answers and the audit reports hang off it, so a different questionnaire is a new procedure
  // (AUDIT0025). A catalogue form picked here would have those criteria written into a shared form.

  //
  // Render helpers
  //
  const footer = (
    <IPStack horizontal={!appContext.isMobileView} verticalAlign="end" tokens={globalStackTokensGapSmall}>
      <IPStack.Item>
        <IPStack horizontal tokens={globalStackTokensGapSmall}>
          <IPPrimaryButton
            disabled={cannotSaveTask() || props.isActionPending}
            onClick={async () => {
              props.onSave(props.task, false);
            }}
          >
            {t('translation:General.Button.Save')}
          </IPPrimaryButton>
          <IPDefaultButton
            onClick={() => {
              onCancel();
            }}
          >
            {t('translation:General.Button.Cancel')}
          </IPDefaultButton>
        </IPStack>
      </IPStack.Item>
    </IPStack>
  );

  const getTaskDetails = () => {
    return (
      <AuditTemplateTaskDetail
        task={props.task}
        orgTask={props.orgTask}
        onUpdate={(newTask: Task) => {
          props.onUpdateTask(newTask);
        }}
        getErrorMessage={validate}
        nameError={nameError}
        isActionPending={props.isActionPending}
        tags={props.tags}
        addTagToTaskState={props.addTagToTaskState}
        removeTagFromTaskState={props.removeTagFromTaskState}
        onChangeCompletionDate={props.onChangeCompletionDate}
        onUpdateTaskForForm={props.onUpdateTaskForForm}
        windowLevel={props.windowLevel}
        navigateExternal={props.navigateExternal}
        canUpdate={props.canUpdate}
        entityNamesByKey={allEntityNamesByKey}
      />
    );
  };

  //
  // Main render
  //
  return (
    <IPModal forceFocusInsideTrap={false} isOpen={props.isOpen} styles={getTaskViewModalStyles(props.windowLevel)}>
      {props.isLoading && <OverlayLoader text={t('task:Progress.Save')} />}
      <IPStack
        verticalFill
        tokens={globalStackTokensGapSmall}
        styles={getTaskViewModalStackStyles(appContext.isMobileView, false)}
      >
        <IPStack.Item>
          <IPStack horizontal verticalAlign="center" horizontalAlign="space-between">
            <IPStack horizontal verticalAlign="center" tokens={globalStackTokensGapSmall}>
              {!appContext.isMobileView && (
                <IPStack.Item>
                  <FontIcon
                    {...complianceIcon}
                    style={{
                      fontSize: 28,
                      color: appContext.useDarkMode ? theme.palette.themeLight : theme.palette.themeSecondary,
                    }}
                  />
                </IPStack.Item>
              )}
              <IPStack.Item>
                <IPText variant="xxLarge">
                  {/* the library IS the list of procedures: nothing else is edited on this level */}
                  {props.task.isSeries() && t('task:Title.AuditTemplate.Procedures')}
                  {props.task.taskId === -1 && !props.task.isSeries() && t('task:Title.AuditTemplate.New')}
                  {props.task.taskId !== -1 && !props.task.isSeries() && t('task:Title.AuditTemplate.Edit')}
                </IPText>
              </IPStack.Item>
            </IPStack>
            {/* the library header has no task actions of its own: it is a container for procedures */}
            {!appContext.isMobileView && !props.task.isSeries() && (
              <IPStack.Item>
                <TaskTopTasksBar
                  task={props.task}
                  onClickRemove={() => setShowRemoveDialog(true)}
                  onSaveAsNewTask={props.onSaveAsNewTask}
                  navigateExternal={props.navigateExternal}
                  canUpdate={props.canUpdate}
                  canDelete={props.canDelete}
                  orgTask={props.orgTask}
                  onConvertChecklist={props.onConvertChecklist}
                  canSave={!hasErrors()}
                  isLoading={props.isLoading}
                  isActionPending={props.isActionPending}
                />
              </IPStack.Item>
            )}
            <IPStack.Item>
              <IPIconButton iconProps={cancelIcon} onClick={onCancel} />
            </IPStack.Item>
          </IPStack>
          {!props.task.isSeries() && (
            <IPStack.Item>
              <TaskDetailOwner
                orgTask={props.orgTask}
                task={props.task}
                onUpdate={props.onUpdateTask}
                canUpdate={props.canUpdate}
              />
            </IPStack.Item>
          )}
          <ErrorMessage error={props.error} customAction={props.onAfterErrorAction} />
          <IPSeparator />
        </IPStack.Item>
        {appContext.isMobileView && !props.task.isSeries() && (
          <IPStack.Item>
            <TaskTopTasksBar
              task={props.task}
              onClickRemove={() => setShowRemoveDialog(true)}
              onSaveAsNewTask={props.onSaveAsNewTask}
              navigateExternal={props.navigateExternal}
              canUpdate={props.canUpdate}
              canDelete={props.canDelete}
              orgTask={props.orgTask}
              onConvertChecklist={props.onConvertChecklist}
              canSave={!hasErrors()}
              isLoading={props.isLoading}
              isActionPending={props.isActionPending}
            />
          </IPStack.Item>
        )}
        {/* The procedure library is nothing but its list of procedures: no form, no checklist, no
            context of its own - those all belong to a single procedure, one level down. */}
        {props.task.isSeries() && <IPStack.Item grow>{getTaskDetails()}</IPStack.Item>}
        {!props.task.isSeries() && (
          <IPStack.Item grow>
            <IPStack horizontal styles={globalStackStylesHeight100} tokens={globalStackTokensGapSmall}>
              {!appContext.isMobileView && (
                <Fragment>
                  <IPStack.Item styles={{ root: { width: '50%' } }}>{getTaskDetails()}</IPStack.Item>
                  <IPSeparator vertical></IPSeparator>
                </Fragment>
              )}
              <IPStack.Item
                styles={!appContext.isMobileView ? { root: { width: '50%' } } : { root: { width: '100%' } }}
              >
                <IPStack verticalFill tokens={globalStackTokensGapSmall}>
                  <IPStack.Item>
                    <IPPivot
                      selectedKey={selectedPivot}
                      onLinkClick={(item) => setSelectedPivot(item?.props.itemKey ?? '')}
                      overflowBehavior="menu"
                    >
                      {appContext.isMobileView && (
                        <IPPivotItem
                          className="isoplanner-task-pivot-details"
                          key="TaskDetails"
                          itemKey="TaskDetails"
                          headerText={t('task:Pivot.TaskDetails')}
                        />
                      )}
                      {!props.task.isSeries() &&
                        props.hasFormFeature &&
                        props.task.taskTypeId &&
                        props.task.taskTypeId > 0 && (
                          <IPPivotItem
                            className="isoplanner-task-pivot-tasktype"
                            key="TaskType"
                            itemKey="TaskType"
                            headerText={
                              TaskType.forTask(
                                props.task.taskTypeId,
                                appContext,
                                t,
                                props.task.taskTypeFamilyId,
                                props.task.formVersion,
                              )?.tabName
                            }
                          />
                        )}
                      {/* the label the generated form's tab carries after the save, so the tab does not
                          change name once the procedure exists */}
                      {showCriteriaTab && (
                        <IPPivotItem
                          className="isoplanner-task-pivot-criteria"
                          key="Criteria"
                          itemKey="Criteria"
                          headerText={t('forms:View.GeneratedFormName')}
                        />
                      )}
                      <IPPivotItem
                        className="isoplanner-task-pivot-library"
                        key="Resources"
                        itemKey="Resources"
                        headerText={t('task:Pivot.Resources')}
                      />
                      {props.task.commentTrailId > 0 && (
                        <IPPivotItem
                          className="isoplanner-task-pivot-activity"
                          key="Activities"
                          itemKey="Activities"
                          headerText={t('task:Pivot.Activities')}
                        />
                      )}
                      {props.task.auditTrailId > 0 && (
                        <IPPivotItem
                          className="isoplanner-task-pivot-changelog"
                          key="Audit"
                          itemKey="Audit"
                          headerText={t('task:Pivot.Audit')}
                        />
                      )}
                    </IPPivot>
                  </IPStack.Item>
                  {selectedPivot === 'TaskDetails' && (
                    <Fragment>
                      <IPStack.Item grow style={{ height: appContext.isMobileView ? '40vh' : 0 }}>
                        {getTaskDetails()}
                      </IPStack.Item>
                    </Fragment>
                  )}
                  {/* The form is what the procedure asks, so it is edited right here rather than
                    previewed with a button that opens the editor in a window of its own. Its changes
                    persist as they are made - the Save below is the task's. */}
                  {selectedPivot === 'TaskType' && formTaskType && (
                    <IPStack.Item grow styles={{ root: { minHeight: 0 } }}>
                      <EmbeddedTaskTypeFormEditor
                        taskType={formTaskType}
                        editable={hasUserDataPermission(
                          appContext,
                          [formTaskType.authSchemaId],
                          AuthSchemaLineOperation.Update,
                        )}
                        entityNamesByKey={allEntityNamesByKey}
                        onFormVersionChanged={onFormVersionChanged}
                      />
                    </IPStack.Item>
                  )}
                  {/* No form yet: the questions are the linked items' audit criteria, edited on the
                    items themselves (entity keyed, nothing saved on this task). What is added or
                    drafted here is what the server puts on the form it generates at the save. The
                    editor body flows, so it sits in a pane of its own inside a positioned host. */}
                  {selectedPivot === 'Criteria' && showCriteriaTab && (
                    <IPStack.Item grow styles={{ root: { minHeight: 0, position: 'relative' } }}>
                      <IPScrollablePane scrollbarVisibility={IPScrollbarVisibility.auto}>
                        <IPStack tokens={globalStackTokensGapSmall} styles={{ root: { paddingRight: 10 } }}>
                          <IPStack.Item>
                            <InfoMessage message={t('audit:Procedure.CriteriaBeforeSave')} />
                          </IPStack.Item>
                          {linkedEntityKeys.map((key) => {
                            const entityKey = auditEntityKeyString(key.entityType, key.entityId);

                            return (
                              <IPStack.Item key={entityKey}>
                                <AuditCriteriaEditorModal
                                  isOpen={true}
                                  usageMode="embedded"
                                  entityType={key.entityType}
                                  entityId={key.entityId}
                                  entityName={allEntityNamesByKey.get(entityKey)}
                                  readonly={!canManageCriteria}
                                  onClose={() => undefined}
                                />
                              </IPStack.Item>
                            );
                          })}
                        </IPStack>
                      </IPScrollablePane>
                    </IPStack.Item>
                  )}
                  {selectedPivot === 'Resources' && (
                    <Fragment>
                      {/* A procedure without a form and without a linked item has nothing to ask yet:
                        the save gives it a form, which then takes fields and a context field. */}
                      {showFormOnSaveHint && (
                        <IPStack.Item>
                          <InfoMessage message={t('audit:Procedure.FormOnSave')} />
                        </IPStack.Item>
                      )}
                      <TaskLinkList
                        taskLinks={props.links}
                        task={props.task}
                        addLinks={props.addLinkToTaskState}
                        setLinks={props.setLinkToTaskState}
                        removeLink={props.removeLinkFromTaskState}
                        navigateExternal={props.navigateExternal}
                      />
                    </Fragment>
                  )}
                  {selectedPivot === 'Activities' && (
                    <IPStack.Item grow>
                      <ActivityTrail
                        auditTrailId={props.task.auditTrailId}
                        commentTrailId={props.task.commentTrailId}
                        taskId={props.task.taskId}
                        authSchemaIds={[props.task.authSchemaId]}
                        onError={props.onError}
                      />
                    </IPStack.Item>
                  )}
                  {selectedPivot === 'Audit' && (
                    <IPStack.Item grow>
                      <AuditTrail auditTrailId={props.task.auditTrailId} />
                    </IPStack.Item>
                  )}
                </IPStack>
              </IPStack.Item>
            </IPStack>
          </IPStack.Item>
        )}
        <IPStack.Item>
          <IPSeparator styles={{ root: { height: 1 } }} />
        </IPStack.Item>
        <IPStack.Item>{footer}</IPStack.Item>
      </IPStack>
      <DialogConfirmDelete
        onNo={() => {
          setShowRemoveDialog(false);
        }}
        onYes={() => {
          setShowRemoveDialog(false);
          props.onRemove();
        }}
        title={props.task.isSeries() ? t('task:Template.RemoveDialogTitle') : t('task:Remove.DialogTitle')}
        subText={props.task.isSeries() ? t('task:Template.RemoveDialogSubText') : t('task:Remove.DialogSubTitle')}
        hidden={!showRemoveDialog}
        confirmText={!props.task.isSeries() ? t('task:Remove.RemoveDialogConfirmText') : undefined}
      />
      <DialogYesNo
        onNo={() => {
          setShowCancelDialog(false);
        }}
        onYes={() => {
          setShowCancelDialog(false);

          props.onCancel();
        }}
        title={t('task:Cancel.DialogTitle')}
        subText={t('task:Cancel.DialogSubTitle')}
        hidden={!showCancelDialog}
      />
    </IPModal>
  );
};

export default AuditTemplateTask;
